import { useEffect, useRef, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { useOutletContext } from "react-router"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { useSofiaEventListener } from "@/hooks/useSofiaEventListener"
import { toast } from "@/hooks/useToast.ts"
import { convertISOCodeToLanguage } from "@/lib/language-to-iso-code"
import { ConsultationReportForm } from "@/modules/patients/detail/consultation-report/consultation-report-form"
import {
  AiContent,
  ConsultationReportSchema,
  consultationReportSchema,
} from "@/modules/patients/detail/consultation-report/schema/consultation-report-schema"
import { consultationReportFragment } from "@/modules/patients/detail/consultation-report/services/consultationReportFragment"
import { useCreateConsultationReportMutation } from "@/modules/patients/detail/consultation-report/services/useCreateConsultationReportMutation"
import { useDeleteConsultationReportMutation } from "@/modules/patients/detail/consultation-report/services/useDeleteConsultationReportMutation"
import { useUpdateConsultationReportMutation } from "@/modules/patients/detail/consultation-report/services/useUpdateConsultationReportMutation"
import { useConsultationReportStoreContext } from "@/modules/patients/detail/consultation-report/store/useConsultationReportStore"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { useLatestDiagnosisCode } from "@/modules/patients/detail/progress-note/hooks/useLatestDiagnosisCode.ts"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam.ts"
import { useDefaultDocumentLanguage } from "@/modules/patients/hooks/useDefaultDocumentLanguage"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam.ts"
import { DoctorType } from "@/modules/patients/information/doctors-network/doctors-network"
import { getDefaultDocumentLanguageValue } from "@/modules/patients/libs/document-languages.ts"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import {
  CH_PROGRESS_NOTE_DOCUMENT_TYPES,
  ChDocumentType,
  ChProgressNoteDocument,
} from "@/modules/patients/types/ProgressNoteDocument"
import { useConfirmStore } from "@/stores/useConfirmStore.ts"
import { useDrawerStore } from "@/stores/useDrawerStore.tsx"

interface ConsultationReportDrawerProps extends FetchKeyProps {
  progressNoteId: string
}

const ConsultationReportDrawer = withErrorBoundary(({ progressNoteId, ...props }: ConsultationReportDrawerProps) => {
  const { t } = useTranslation()
  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const appointmentId = useAppointmentParam()

  const [patientData] = usePatientByIdQuery(patientId, countryCode, appointmentId)
  const patient = patientData.patientById
  const doctors = patient?.__typename === "ch_Patient" ? patient.doctorNetworks : []

  const { patient: patientFragment } = useOutletContext<{ patient: PatientFragment$data }>()

  const consultationReportRef = useConsultationReportStoreContext((state) => state.consultationReportRef)
  const setConsultationReportRef = useConsultationReportStoreContext((state) => state.setConsultationReportRef)
  const reportData = useFragment(consultationReportFragment, consultationReportRef)

  const { diagnosisCode: latestDiagnosisCode } = useLatestDiagnosisCode(progressNoteId || undefined)
  const { documentLanguage: defaultDocumentLanguage } = useDefaultDocumentLanguage()
  const isEditMode = !!reportData?.id
  const updatedAt = reportData?.updatedAt || reportData?.createdAt
  const updatedBy = reportData?.updatedBy || reportData?.createdBy

  const isSavedCpInDoctors = reportData?.healthcareProviderId
    ? doctors.some((d) => d.careProvider.id === reportData.healthcareProviderId)
    : true

  const documentData = reportData
    ? {
        diagnosisCode: reportData.diagnosisCode,
        language: getDefaultDocumentLanguageValue(reportData.language),
        healthcareProviderId: isSavedCpInDoctors ? reportData.healthcareProviderId : "",
        anamnesis: reportData.anamnesis,
        recommendedTreatment: reportData.recommendedTreatment,
        isAiGenerated: reportData.isAiGenerated,
        isReviewed: reportData.isAiGenerated,
      }
    : null

  const defaultCareProviderId = getDefaultCareProviderId(doctors)
  const defaultCareProvider = doctors.find((d) => d.careProvider.id === defaultCareProviderId)?.careProvider
  const defaultLanguageForNewReport =
    getCareProviderSingleLanguage(defaultCareProvider?.officialLanguages) ?? defaultDocumentLanguage

  const form = useForm<ConsultationReportSchema>({
    mode: "all",
    resolver: zodResolver(consultationReportSchema),
    defaultValues: documentData ?? {
      diagnosisCode: latestDiagnosisCode,
      language: defaultLanguageForNewReport,
      healthcareProviderId: defaultCareProviderId,
      anamnesis: "",
      recommendedTreatment: "",
      isAiGenerated: false,
      isReviewed: false,
    },
  })

  const [lastAiContent, setLastAiContent] = useState<AiContent | null>(() =>
    reportData?.isAiGenerated && !reportData.isAiContentModified
      ? { anamnesis: reportData.anamnesis, recommendedTreatment: reportData.recommendedTreatment }
      : null,
  )

  const initialResetDone = useRef(false)

  useEffect(() => {
    if (reportData && !initialResetDone.current) {
      initialResetDone.current = true
      if (!isSavedCpInDoctors) {
        form.setValue("healthcareProviderId", "", { shouldDirty: true })
      }
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [doctors, form, isSavedCpInDoctors, reportData])

  const {
    formState: { isDirty },
  } = form
  useDirtyDrawerMaker(isDirty)

  const [createReport, isCreating] = useCreateConsultationReportMutation()
  const [updateReport, isUpdating] = useUpdateConsultationReportMutation()
  const [deleteReport] = useDeleteConsultationReportMutation()

  const [isAiRunning, setIsAiRunning] = useState(false)
  useSofiaEventListener("AI_FEATURE_RUNNING_EVENT", (e) => {
    setIsAiRunning(e.detail)
  })

  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)

  const handleCloseDrawer = () => {
    closeDrawer()
    setConsultationReportRef(null)
  }

  const onSubmit = ({ isReviewed: _isReviewed, ...data }: ConsultationReportSchema) => {
    if (!progressNoteId) return

    const isAiContentModified = (() => {
      if (reportData?.isAiContentModified) return true
      if (!data.isAiGenerated) return false
      if (!lastAiContent) return false
      return (
        data.anamnesis !== lastAiContent.anamnesis || data.recommendedTreatment !== lastAiContent.recommendedTreatment
      )
    })()

    if (isEditMode) {
      updateReport({
        variables: {
          input: {
            id: reportData.id,
            anamnesis: data.anamnesis,
            diagnosisCode: data.diagnosisCode,
            healthcareProviderId: data.healthcareProviderId,
            isAiGenerated: data.isAiGenerated,
            language: data.language,
            recommendedTreatment: data.recommendedTreatment,
            isAiContentModified,
          },
        },
        onCompleted: handleCloseDrawer,
      })
    } else {
      createReport({
        variables: {
          input: {
            patientId,
            progressNoteId,
            anamnesis: data.anamnesis,
            diagnosisCode: data.diagnosisCode,
            healthcareProviderId: data.healthcareProviderId,
            isAiGenerated: data.isAiGenerated,
            language: data.language,
            recommendedTreatment: data.recommendedTreatment,
            isAiContentModified,
          },
        },
        onCompleted: handleCloseDrawer,
        updater(store, data) {
          try {
            const progressNoteRecordProxy = store.get(progressNoteId)
            const consultationReport = data?.ch_createConsultationReport?.ch_ConsultationReport

            if (consultationReport?.id) {
              const newRecordProxy = store.get(consultationReport.id)
              if (newRecordProxy) {
                progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "ch_consultationReport")
              }
            }
          } catch (error) {
            console.error("Error while processing data:", error)
          }
        },
      })
    }
  }

  const handleDelete = () => {
    if (!progressNoteId || !reportData) return

    confirm({
      confirmText: "common.button.delete",
      cancelText: "common.button.keep",
      title: `${t("common.button.delete")} ${t("patient.consultationReport.title")}`,
      content: "episodeOfCare.deleteProgressNote.content",
      onConfirm: () =>
        deleteReport({
          variables: {
            input: {
              id: reportData.id,
            },
          },
          onCompleted: () => {
            cancel()
            handleCloseDrawer()
            toast({
              description: t("patient.consultationReport.deleteMessage"),
            })
          },
        }),
    })
  }

  return (
    <Drawer open onOpenChange={handleCloseDrawer}>
      <FormProvider {...form}>
        <DrawerContent>
          <DrawerClose className="top-2 right-2" />
          <DrawerHeader className="mb-4 pr-4" data-testid="consultation-report-header">
            <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
              {t("patient.consultationReport.title")}
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody className="flex gap-4 pt-0.5" data-testid="consultation-report-body">
            <div className="grid gap-8">
              {!!progressNoteId && (
                <ConsultationReportForm
                  fetchKey={props.fetchKey}
                  doctors={doctors}
                  appointmentId={appointmentId}
                  progressNoteId={progressNoteId}
                  isEditMode={isEditMode}
                  onAiSuccess={setLastAiContent}
                  patientAge={patientFragment.age.years}
                  patientGender={patientFragment.gender}
                />
              )}
            </div>
          </DrawerBody>
          <DrawerFooter className="flex-wrap gap-2" data-testid="consultation-report-footer">
            {isEditMode && (
              <div className="flex gap-2">
                {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                <button onClick={handleDelete} data-testid="consultation-report-delete-button">
                  <TrashSimple className="text-on-surface-subdued" />
                </button>
              </div>
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="secondary" data-testid="consultation-report-cancel-button" onClick={handleCloseDrawer}>
                {t("common.button.cancel")}
              </Button>
              <Button
                disabled={(isEditMode && !isDirty) || isAiRunning}
                onClick={() => {
                  FaroInsightsWrapper.pushEvent("documents_consultation_report_save")
                  form.handleSubmit(onSubmit)()
                }}
                data-testid="consultation-report-save-button"
                isLoading={isCreating || isUpdating}>
                {t("common.button.save")}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </FormProvider>
    </Drawer>
  )
}, "ConsultationReportDrawer")

function getDefaultCareProviderId(doctors: DoctorType): string {
  if (doctors.length === 1) {
    return doctors[0].careProvider.id
  }
  const gatekeeper = doctors.find((d) => d.role === "GATEKEEPER")
  if (gatekeeper) {
    return gatekeeper.careProvider.id
  }
  return ""
}

function getCareProviderSingleLanguage(officialLanguages?: DoctorType[number]["careProvider"]["officialLanguages"]) {
  if (officialLanguages?.length !== 1) return null
  const language = convertISOCodeToLanguage(officialLanguages[0].isoLanguageCode)
  if (!language || language === "ENGLISH") return null
  return language
}

const EXCLUDED_DOCUMENT_TYPES: ChDocumentType[] = [
  "carePlanSummary",
  "medicalReportAttachments",
  "ch_consultationReport",
]

function collectConsultationReportDocuments(progressNote: ChProgressNoteDocument | undefined): string[] {
  if (!progressNote) return []
  return CH_PROGRESS_NOTE_DOCUMENT_TYPES.filter((type) => !EXCLUDED_DOCUMENT_TYPES.includes(type) && progressNote[type])
}

export { ConsultationReportDrawer, collectConsultationReportDocuments }
